import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiMutation } from '../api/hooks';
import { ApiError } from '../api/problem';
import type { ChangePasswordRequest } from '../api/types';
import { MINIMUM_PASSWORD_LENGTH } from '../auth/passwords';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';

/**
 * Changing one's own password (FR-105).
 *
 * Every account starts with a password somebody else chose — the administrator who added the
 * person — so this is the screen that makes it theirs. The current password is asked for even
 * though they are signed in: a reception machine left unlocked is not enough to take an account
 * over.
 *
 * The server signs out every other session as part of the change and keeps this one, so the
 * screen stays where it is and says so rather than bouncing to sign-in.
 */
export function ChangePasswordPage() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [done, setDone] = useState(false);

  const change = useApiMutation<void, ChangePasswordRequest>((body) => ({
    path: '/me/password',
    options: { method: 'POST', body },
  }));

  const tooShort = next.length > 0 && next.length < MINIMUM_PASSWORD_LENGTH;
  const mismatch = confirmation.length > 0 && next !== confirmation;
  // A wrong current password is the one mistake worth saying beside the field it belongs to. The
  // server names the field; anything else falls through to the general error below the form.
  const wrongCurrent = change.error instanceof ApiError && Boolean(change.error.fieldErrors.currentPassword);

  return (
    <>
      <PageHeader title={t('account.changePassword')} description={t('account.description')} />

      {done ? (
        <div className="callout" style={{ marginBlockEnd: 'var(--space-5)' }} role="status">
          <p>{t('account.done')}</p>
        </div>
      ) : null}

      <form
        className="panel"
        style={{ maxWidth: '32rem' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (tooShort || mismatch) {
            return;
          }
          setDone(false);
          change.mutate(
            { currentPassword: current, newPassword: next },
            {
              onSuccess: () => {
                setDone(true);
                setCurrent('');
                setNext('');
                setConfirmation('');
              },
            },
          );
        }}
      >
        <label className="field">
          <span className="field__label">{t('account.currentPassword')}</span>
          <input
            type="password"
            className="field__input"
            value={current}
            required
            autoComplete="current-password"
            aria-invalid={wrongCurrent}
            onChange={(event) => {
              setCurrent(event.target.value);
              if (change.error) {
                change.reset();
              }
            }}
          />
          {wrongCurrent ? <span className="field__error">{t('account.currentPasswordWrong')}</span> : null}
        </label>

        <label className="field">
          <span className="field__label">{t('account.newPassword')}</span>
          <input
            type="password"
            className="field__input"
            value={next}
            required
            minLength={MINIMUM_PASSWORD_LENGTH}
            maxLength={200}
            autoComplete="new-password"
            onChange={(event) => setNext(event.target.value)}
          />
          {/* The button stays disabled while the password is short, so the reason has to be
              visible: a hint that looks the same met or unmet leaves a dead button and no clue. */}
          {tooShort ? (
            <span className="field__error">
              {t('account.tooShort', { n: MINIMUM_PASSWORD_LENGTH, length: next.length })}
            </span>
          ) : (
            <span className="field__hint">{t('account.passwordHint', { n: MINIMUM_PASSWORD_LENGTH })}</span>
          )}
        </label>

        <label className="field">
          <span className="field__label">{t('account.confirmPassword')}</span>
          <input
            type="password"
            className="field__input"
            value={confirmation}
            required
            autoComplete="new-password"
            onChange={(event) => setConfirmation(event.target.value)}
          />
          {mismatch ? <span className="field__error">{t('account.mismatch')}</span> : null}
        </label>

        {change.error && !wrongCurrent ? <ErrorState error={change.error} /> : null}

        <button
          type="submit"
          className="button button--primary"
          style={{ marginBlockStart: 'var(--space-4)' }}
          disabled={change.isPending || tooShort || mismatch}
        >
          {change.isPending ? t('account.submitting') : t('account.changePassword')}
        </button>
      </form>
    </>
  );
}
